import { Chip } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import StatusChip from '@/cam-admin/paywall/StatusChip';
import { notificationSceneById } from '@/cam-admin/notifications/Notifications.scenes';
import { shortlistScenes } from '@/cam-admin/paywall/Shortlist.scenes';
import { providerScenes } from '@/cam-admin/paywall/Provider.scenes';
import { type ShortlistInitial } from '@/cam-admin/paywall/useShortlists';
import {
  F2Email,
  F3Paywall,
  F4Answers,
  F6Unlocked,
  F7Push,
  F11Timeout,
  S2PaymentFailed,
  S4Declined,
} from '@/cam-admin/explore/paywall/Flow';
import ShortlistsV1Screen from '@/karehero-app/care/shortlists/ShortlistsV1Screen';
import NotificationsScreen from '@/karehero-app/notifications/NotificationsScreen';
import { shortlistSceneById as memberSceneById } from '@/karehero-app/care/shortlists/Shortlists.scenes';
import { SHORTLIST_STARTED } from '@/karehero-app/notifications/Notifications.fixtures';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// The CAM half: one live control panel per step, opened on the Shortlists tab and
// jumped straight to a state through the same initial prop the live state select
// uses. Same reasoning as cam-care-search.tsx — the handover and the stage read
// one catalog, so a state cannot mean two things.
const cam = (initial?: ShortlistInitial) => (
  <ControlPanel
    embedded
    initialTab='shortlist'
    withShortlist
    shortlist={initial}
  />
);

const camScene = (id: string) =>
  cam(shortlistScenes.find((s) => s.id === id)?.initial);

// The CAM's own feed, opened from the bell over the shell. Same scene catalog the
// notifications page and its handover read, so a state cannot mean two things.
const camNotifications = () => (
  <ControlPanel
    embedded
    initialView='notifications'
    notificationsInitial={notificationSceneById('feed').initial}
  />
);

// The family's side, on the Search tab where shortlists live. Same components the
// member app ships and the same scene catalog its own stage reads, so a state
// cannot mean one thing here and another there.
const memberScene = (id: string) => (
  <ShortlistsV1Screen initial={memberSceneById(id).initial} />
);

// A scene's own repro line, so the caption under a mini is the one written in the
// catalog rather than a second description of the same state.
const repro = (scenes: { id: string; repro?: string }[], id: string): string =>
  scenes.find((s) => s.id === id)?.repro ?? '';

const camRepro = (id: string) => repro(shortlistScenes, id);
const providerRepro = (id: string) => repro(providerScenes, id);

// The provider half. Sketch fidelity, and the frames live in the exploration
// folder because that is where they were drawn: the provider stage renders these
// same exports rather than redrawing them, and so does this page.
const NOTION = 'https://app.notion.com/p';

// Project MC: the blurred-profile paywall, both halves on one page. The CAM side
// is the real prototype, the provider side is sketch fidelity, and the carer side
// is somebody else's handover — linked rather than restated.
export const projectMcSpec: HandoverSpec = {
  id: 'project-mc',
  title: 'Blurred-profile paywall',
  screen: 'desktop',
  description:
    'A dom care provider gets their own link, sees an anonymised client profile, and pays to unlock the lead. Every dom provider is messaged, premium or not. CAM gets two actions and nothing else: publish and send, then resend. Publishing is what sends, so nobody has paid when a list goes live: a provider becomes visible to the family by paying, or by a CAM making them visible without one.',
  links: [
    {
      label: 'Mini PRD (Notion)',
      url: `${NOTION}/3b563e8971dd81a8894bd053e44926b8`,
    },
    {
      label: 'Feature spec (Notion)',
      url: `${NOTION}/3bc63e8971dd81b4a674f6bf9d0aa704`,
    },
    {
      label: 'Project MC home (Notion)',
      url: `${NOTION}/3b563e8971dd8180b27ac1dedc670432`,
    },
    { label: 'CAM half, walkable', url: '/paywall-shortlist' },
    { label: 'Provider half, walkable', url: '/paywall-provider' },
    { label: 'Exploration writeup', url: '/explore/blurred-profile-paywall' },
    { label: 'What the carer sees', url: '/handover/care-shortlists' },
    // Both notification surfaces, because the build needs them: publishing is the
    // send, so the carer is waiting and the CAM is watching silence. The rows for
    // this project are seeded in each screen's fixtures.
    { label: 'CAM notifications', url: '/care-search/notifications' },
    { label: 'Member notifications', url: '/karehero-app/notifications' },
  ],
  flows: [
    {
      journey: 'Provider',
      title: 'The provider’s journey',
      steps: [
        {
          id: 'email',
          title: 'The email lands',
          tap: 'CAM messages every dom provider. One link, one provider, one lead',
          render: () => <F2Email />,
        },
        {
          id: 'paywall',
          title: 'Blurred profile',
          tap: 'Follow the link in the email',
          render: () => <F3Paywall />,
        },
        {
          id: 'answers',
          title: 'Answering and paying',
          tap: 'Tap ‘Unlock this lead’',
          render: () => <F4Answers />,
        },
        {
          id: 'unlocked',
          title: 'Unlocked',
          tap: 'Stripe confirms, and not before',
          render: () => <F6Unlocked />,
        },
      ],
    },
    {
      journey: 'Provider',
      title: 'The other answer',
      steps: [
        {
          id: 'decline',
          title: 'Can’t take this one',
          tap: 'One tap, from the email or from the page',
          render: () => <S4Declined />,
        },
      ],
    },
    {
      journey: 'CAM',
      title: 'The two CAM actions: publish and send, then resend',
      steps: [
        {
          id: 'fresh',
          title: 'A draft nobody has been told about',
          tap: 'The list is assembled, nothing has gone out',
          render: () => camScene('fresh'),
        },
        {
          id: 'publish-send',
          title: 'Publish and send',
          tap: 'Tap Publish and send. The request, pulled from the care assessment and editable, with the count of who it reaches',
          render: () => camScene('publish-send'),
        },
        {
          id: 'send-preview',
          title: 'Reading it as they will get it',
          tap: 'Tap Preview',
          render: () => camScene('send-preview'),
        },
        {
          id: 'hidden-unpaid',
          title: 'Live, and every row hidden',
          tap: 'Publish and send fires. Links out, nothing back, and the family is looking at an empty shortlist',
          render: () => camScene('hidden-unpaid'),
        },
        {
          id: 'first-paid',
          title: 'First payment in',
          tap: 'Stripe confirms for one provider, who turns Visible on their own',
          render: () => camScene('first-paid'),
        },
        {
          id: 'published',
          title: 'Published to the family',
          tap: 'What the family can see: whoever has paid, plus whoever a CAM let through',
          render: () => camScene('published'),
        },
      ],
    },
    {
      journey: 'CAM',
      title: 'Reading a row, and typing up a call',
      steps: [
        {
          id: 'row',
          title: 'Reading a row',
          tap: 'Click any row on the Shortlists tab',
          render: () =>
            cam({ scenario: 'firstPaid', openRow: 'oakwood-care-home' }),
        },
        {
          id: 'row-empty',
          title: 'A row nobody has answered',
          tap: 'Click a row on a list where nothing has come back',
          render: () => camScene('cam-entry-empty'),
        },
        {
          id: 'row-cam-entered',
          title: 'Typed up from a phone call',
          tap: 'Fill the five fields in and save',
          render: () => camScene('cam-entered'),
        },
      ],
    },
    {
      journey: 'CAM',
      title: 'Hearing back',
      steps: [
        {
          id: 'notified',
          title: 'A provider answers',
          tap: 'The bell is how the CAM hears it: paid, declined, or five providers silent for three days',
          render: () => camNotifications(),
        },
      ],
    },
    {
      journey: 'Member',
      // Phone frames in a desktop handover: this half of the feature is the
      // member app, and the CAM screens either side of it are not.
      screen: 'phone',
      title: 'What the family sees, in order',
      steps: [
        {
          id: 'm-started',
          title: 'The shortlist starts',
          tap: 'Publish and send fires. The family hears that work is underway, not that a shortlist is ready',
          render: () => (
            <F7Push
              title={SHORTLIST_STARTED.title}
              body={SHORTLIST_STARTED.body}
            />
          ),
        },
        {
          id: 'm-waiting',
          title: 'Waiting to hear back',
          tap: 'Open the notification',
          render: () => memberScene('view-waiting'),
        },
        {
          id: 'm-tab',
          title: 'The shortlist on the tab',
          tap: 'Or reach it from Search',
          render: () => memberScene('home'),
        },
        {
          id: 'm-push',
          title: 'A provider pays',
          tap: 'Stripe confirms, and the push is the first news of it',
          render: () => <F7Push />,
        },
        {
          id: 'm-notifications',
          title: 'In the notification centre',
          tap: 'Or found later, from the bell',
          render: () => <NotificationsScreen />,
        },
        {
          id: 'm-filling',
          title: 'Providers to choose from',
          tap: 'Open the shortlist again',
          render: () => memberScene('view-small'),
        },
      ],
    },
  ],
  states: [
    {
      journey: 'Provider',
      id: 'p-email',
      title: 'The email lands',
      kind: 'happy',
      repro: providerRepro('email'),
      render: () => <F2Email />,
    },
    {
      journey: 'Provider',
      id: 'p-paywall',
      title: 'Blurred profile',
      kind: 'happy',
      repro: providerRepro('paywall'),
      render: () => <F3Paywall />,
    },
    {
      journey: 'Provider',
      id: 'p-answers',
      title: 'Answering the request',
      kind: 'happy',
      repro: providerRepro('answers'),
      render: () => <F4Answers />,
    },
    {
      journey: 'Provider',
      id: 'p-answers-returning',
      title: 'Answering, second time on',
      kind: 'happy',
      repro: providerRepro('answers-returning'),
      render: () => <F4Answers isReturning />,
    },
    {
      journey: 'Provider',
      id: 'p-unlocked',
      title: 'Unlocked',
      kind: 'happy',
      repro: providerRepro('unlocked'),
      render: () => <F6Unlocked />,
    },
    {
      journey: 'CAM',
      id: 'c-fresh',
      title: 'Nothing messaged yet',
      kind: 'happy',
      repro: camRepro('fresh'),
      render: () => camScene('fresh'),
    },
    {
      journey: 'CAM',
      id: 'c-some-sent',
      title: 'One added since the send',
      kind: 'happy',
      repro: camRepro('some-sent'),
      render: () => camScene('some-sent'),
    },
    {
      journey: 'CAM',
      id: 'c-first-paid',
      title: 'First payment in',
      kind: 'happy',
      repro: camRepro('first-paid'),
      render: () => camScene('first-paid'),
    },
    {
      journey: 'CAM',
      id: 'c-published',
      title: 'Published to the family',
      kind: 'happy',
      repro: camRepro('published'),
      render: () => camScene('published'),
    },
    {
      // Not a state of the paywall table: the other kind of list, on the same
      // tab, with the paywall taken out. It leads the states for the same reason
      // it leads the scene select.
      journey: 'CAM',
      id: 'c-care-home-list',
      title: 'Care home list — no paywall',
      kind: 'view',
      repro: camRepro('care-home-list'),
      render: () => camScene('care-home-list'),
    },
    {
      journey: 'CAM',
      id: 'c-cam-entered',
      title: 'Typed up from a phone call',
      kind: 'happy',
      repro: camRepro('cam-entered'),
      render: () => camScene('cam-entered'),
    },
    {
      journey: 'CAM',
      id: 'c-cam-entry-empty',
      title: 'Nothing on a row yet',
      kind: 'empty',
      repro: camRepro('cam-entry-empty'),
      render: () => camScene('cam-entry-empty'),
    },
    {
      journey: 'CAM',
      id: 'c-one-declined',
      title: 'One no, four nothings',
      kind: 'happy',
      repro: camRepro('one-declined'),
      render: () => camScene('one-declined'),
    },
    {
      journey: 'CAM',
      id: 'c-all-quiet',
      title: 'Nobody has answered',
      kind: 'empty',
      repro: camRepro('all-quiet'),
      render: () => camScene('all-quiet'),
    },
    {
      journey: 'CAM',
      id: 'c-publish-send',
      title: 'Publish and send',
      kind: 'edge',
      repro: camRepro('publish-send'),
      render: () => camScene('publish-send'),
    },
    {
      journey: 'CAM',
      id: 'c-hidden-unpaid',
      title: 'Published, nobody has paid',
      kind: 'edge',
      repro: camRepro('hidden-unpaid'),
      render: () => camScene('hidden-unpaid'),
    },
    {
      journey: 'CAM',
      id: 'c-row-drawer',
      title: 'A row, opened',
      kind: 'edge',
      repro: 'firstPaid, open on the paid row.',
      render: () =>
        cam({ scenario: 'firstPaid', openRow: 'oakwood-care-home' }),
    },
    {
      journey: 'Provider',
      id: 'p-declined',
      title: 'Can’t take this one',
      kind: 'happy',
      repro: providerRepro('declined'),
      render: () => <S4Declined />,
    },
    {
      journey: 'Provider',
      id: 'p-timeout',
      title: 'Lead has closed',
      kind: 'edge',
      repro: providerRepro('timeout'),
      render: () => <F11Timeout />,
    },
    {
      journey: 'Provider',
      id: 'p-failed',
      title: 'Something went wrong',
      kind: 'error',
      repro: providerRepro('failed'),
      render: () => <S2PaymentFailed />,
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-waiting',
      title: 'Published, nobody has confirmed',
      kind: 'empty',
      // The normal first hours of every shortlist now, not an edge case.
      repro:
        'A published shortlist with no providers on it yet. The waiting is said out loud rather than left as a blank screen',
      render: () => memberScene('view-waiting'),
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-tab-waiting',
      title: 'The tab, while waiting',
      kind: 'empty',
      repro:
        'The card reads “Waiting on providers” rather than “0 providers”, which would read as a list that came back empty',
      render: () => memberScene('home'),
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-started-push',
      title: 'Published, as a push',
      kind: 'happy',
      repro:
        'Fires when the CAM publishes, before anybody has paid. It names the wait rather than announcing a shortlist: “your shortlist is ready” on an empty list is the version we ruled out',
      render: () => (
        <F7Push title={SHORTLIST_STARTED.title} body={SHORTLIST_STARTED.body} />
      ),
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-push',
      title: 'A new provider, as a push',
      kind: 'happy',
      repro:
        'The second push, when the waiting ends. Later providers are grouped rather than sent one at a time',
      render: () => <F7Push />,
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-notifications',
      title: 'The notification centre',
      kind: 'happy',
      repro:
        'All three member notifications: the first provider, the later ones grouped, and the three-day wait. Copy is a first draft',
      render: () => <NotificationsScreen />,
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-filling',
      title: 'Providers to choose from',
      kind: 'happy',
      repro:
        'The same list once providers have paid, or a CAM has made them visible',
      render: () => memberScene('view-small'),
    },
    {
      journey: 'Member',
      screen: 'phone',
      id: 'm-no-shortlist',
      title: 'No shortlist at all',
      kind: 'empty',
      repro:
        'Before a care expert has published anything. Different from waiting: there is no list yet, rather than a list with nobody on it',
      render: () => memberScene('home-empty'),
    },
  ],
  requirements: [
    {
      text: 'Paywall page showing an anonymised client profile: funding type, care needs summary, complexity indicators. Enough to judge fit, no PII',
      status: 'built',
      where: 'Blurred profile',
      note: 'The real values render underneath the blur so the provider sees the shape of what they are buying. Which fields sit either side of the blur is Ling’s list, not ours',
    },
    {
      text: 'Every dom provider gets messaged, premium or not. Tier is data we hold about a provider rather than a gate',
      status: 'built',
      where: 'Publish and send',
      note: 'No premium gate on the send, which is what collapsed communications and status into one model',
    },
    {
      text: 'A single status model per provider against a shortlist: messaged, paid, declined',
      status: 'built',
      where: 'Status model table, and the Status column on every row',
      note: 'The prototype carries a fourth, draft, for a list before its send and for a provider added after it',
    },
    {
      text: 'CAM actions are publish and send, then resend. Two actions and nothing else: publishing the list is what emails every provider on it',
      status: 'built',
      where: 'The three CAM actions',
      note: 'Resend is the row action once everyone has been messaged, so the page-level pair stays at two',
    },
    {
      text: 'Publish opens a modal of what providers will be sent, pulled from the care assessment and editable, with an additional info box and a preview of the email',
      status: 'built',
      where: 'The publish decision',
      note: 'A row per provider, and deliberately no tick-everything control: the moment a quiet list clears in one click, the exception becomes the default',
    },
    {
      text: 'CAM can untick individual providers to hold them back, and can hold back a paid provider as well as let an unpaid one through',
      status: 'built',
      where: 'The publish decision',
      note: 'Nothing in the confirm is disabled. Holding a paid provider back does not take away the lead they bought',
    },
    {
      text: 'No cap on paid providers. The five-place shortlist is gone',
      status: 'built',
      where: 'Everywhere. Nothing counts places',
    },
    {
      text: 'CAM can tell a provider with no room from one who never opened the email',
      status: 'partial',
      note: 'Only if they tap the no. A provider who says nothing stays messaged whatever the reason, and the ring-round that used to establish it is gone. The free no is the mitigation, not a fix',
    },
    {
      text: 'Availability confirmed against this specific request, tapped not typed, prefilled from the care request so the answer is a confirm or a correction',
      status: 'built',
      where: 'Answering and paying',
      note: 'Blocked on the confirmed field list. Open question 1',
    },
    {
      text: 'Answering and paying are one action, with the itemised total beside the questions',
      status: 'built',
      where: 'Answering and paying',
      note: 'A page break in the middle of that decision invited the provider to leave',
    },
    {
      text: 'Payment failed keeps their place and their answers, and no provider enters paid unless Stripe confirms',
      status: 'built',
      where: 'Something went wrong',
      note: 'One screen for anything that breaks between the pay button and Stripe: declined card, dropped session, our error',
    },
    {
      text: 'A lead that is over explains rather than 404s, whether the link aged out or the client has already arranged their care',
      status: 'built',
      where: 'Lead has closed',
      note: 'One screen for both. They were specced as two on the argument that a wasted trip reads differently from an old link, and it does not: the provider has nothing to do about either, so a second screen would only be a second way of saying so',
    },
    {
      text: 'A provider who has already paid and clicks their link again lands on the lead, not the paywall and not an error',
      status: 'built',
      where: 'Unlocked',
      note: 'The unlocked screen is reused rather than a screen of its own: nothing has changed for them, and anything new here would read as their lead having moved',
    },
    {
      text: 'Pay-to-unlock via Stripe releases the client’s email and access to their case manager. Email only, no phone number',
      status: 'partial',
      where: 'Answering and paying, Unlocked',
      note: 'The rail and the unlocked screen name what is being bought, but there is no Stripe and no checkout. Case-manager access has to be nameable as a line item before it can be one. Open question in the Mini PRD',
    },
    {
      text: 'Named contact, direct number and terms asked on a first unlock and never again. Not an overlay: the block sits at the top of the answers page, so the second lead opens straight onto the questions with nothing else moved',
      status: 'built',
      where: 'Answering and paying, and Answering second time on',
      note: 'Both versions of the page are drawn, which is the only way the never-again half is a design rather than a promise',
    },
    {
      text: 'A one-tap “can’t take this one” on every screen that shows the lead, and a screen confirming it',
      status: 'built',
      where:
        'Under the price on Blurred profile and on Answering and paying, then Can’t take this one',
      note: 'Not on the email, which asks one thing and nothing else: a no given before the profile has been read is a guess, and it costs us a provider on a request they might have been able to take. No reason picker either — one more question is what turns a free no back into silence',
    },
    {
      text: 'A proper invoice, and the lead findable again a week later',
      status: 'partial',
      where: 'Unlocked',
      note: 'Both are buttons with nothing behind them. Where the lead lives after checkout is undecided: email only, or a page they return to',
    },
    {
      text: 'A provider who pays after the list has gone out is published by CAM, from the row',
      status: 'built',
      where: 'Make visible, on a row of a list that has already gone out',
      note: 'Settled against the spec, which said late payers join on their own with no second publish action. They do not: a paid provider appearing on the carer’s shortlist is a change to what the carer sees, and CAM makes it. One row, one click, no confirm, because the CAM is looking at the provider they are deciding about',
    },
    {
      text: 'Published is a status on the shortlist and means the links have gone out. A provider is separately Visible or Hidden to the family, which turns on payment or on a CAM override',
      status: 'built',
      where:
        'Shortlist selector for the list, Visibility column for the provider',
      note: 'Two different questions, and the second only exists once the first is answered. Every row on a freshly published list is Hidden, because publishing is the send and nobody has paid yet. Paying turns a row Visible on its own; Make visible is the override for the provider a CAM backs anyway',
    },
    {
      text: 'CAM can enter the same five fields a provider would have filled in themselves, from the row drawer, stamped with who entered them and when',
      status: 'built',
      where: 'Typed up from a phone call, and Nothing on a row yet',
      note: 'Behind an outlined button in the drawer footer rather than open on arrival, so reading a row never puts it into an editing state. Committed on save rather than as you type: a half-typed number carrying somebody’s name reads as a fact. Entering answers does not move the provider’s state, because answering a CAM is not paying',
    },
    {
      text: 'CAM can see whether details came from the provider or from a colleague, and CAM-entered detail never reaches the carer',
      status: 'partial',
      where: 'The answers block in the drawer',
      note: 'The record carries who entered each answer and when, and it decides what edit mode lets a CAM touch: provider-entered answers cannot be overwritten. But nothing on the screen says which kind a row is holding, so a CAM reading it cannot tell — the stamp and the not-shown-to-the-carer line were both taken off',
    },

    {
      text: 'CAM knows when a shortlist is not filling, before the carer notices',
      status: 'not-built',
      note: 'The threshold and what CAM does about it are both open',
    },
    {
      text: 'Carer consent to share their email with a provider',
      status: 'out-of-scope',
      where: 'Sign up, in the member app',
      note: 'Both specs left this open and it is not: the carer gives it when they sign up, in the terms and privacy policy they accept there. So there is nothing for this project to build and nothing to add to the publish step — asking a second time at the point a shortlist goes out would suggest the first ask did not count',
    },
    {
      text: 'What the carer sees when the shortlist publishes',
      status: 'out-of-scope',
      where: 'Its own handover: care-shortlists',
      note: 'Nothing about that screen changes here, only who is allowed onto it. The push and the published list are frames in the exploration writeup',
    },
    {
      text: 'The quality gate and its not-eligible state',
      status: 'out-of-scope',
      note: 'In or out is undecided. If it goes in, someone has to set the thresholds and it needs a rejection screen',
    },
    {
      text: 'Provider dashboard, and Otta-style performance scoring feeding shortlist priority',
      status: 'out-of-scope',
      note: 'Stretch in the Mini PRD',
    },
    {
      text: 'Care homes',
      status: 'out-of-scope',
      note: 'Dom care only. Care requests do not map the same way and the shortlist mechanics differ, so it gets its own spec',
    },
    {
      text: 'Commercial contracts, T&Cs, refund terms, pricing, and the anonymised field list',
      status: 'out-of-scope',
      note: 'Steph owns the commercial side, Ling owns the fields. We build to whatever they spec',
    },
  ],
  components: {
    mui: [
      'Box',
      'Stack',
      'Typography',
      'Paper',
      'Table',
      'Chip',
      'Divider',
      'TextField',
      'Checkbox',
      'FormControlLabel',
    ],
    existing: [
      'Button',
      'Icon',
      'Drawer',
      'Modal',
      'OptionCard',
      'ControlPanel',
    ],
    new: [
      'ShortlistTab',
      'StatusChip',
      'PublishAndSend',
      'RowDrawer',
      'CamAnswerForm',
      'CheckoutRail',
    ],
  },
  componentsNote:
    'The two halves are not at the same fidelity, and that is the thing to know before quoting this. The CAM half is the real prototype: ShortlistTab, StatusChip, PublishAndSend, SendPreview and RowDrawer are working components against a state model. The provider half is sketch fidelity and lives in the exploration folder, with static frames and no checkout, so promoting it means moving those frames out of explore and building the real thing behind them. The provider side got smaller rather than bigger: the answers page collects nothing about the request and no longer asks for a confirm, because paying is the yes. The CAM side got one screen larger, and it is the one to scope carefully — publishing now writes the outbound message, so the modal and its preview are the new build.',
  componentPreviews: {
    StatusChip: () => <StatusChip state='paid' isPublished />,
    Chip: () => <Chip size='small' color='sunflower' label='Unpaid' />,
    Button: () => (
      <Button ariaLabel='Publish and send' size='sm' onPress={noop}>
        Publish and send
      </Button>
    ),
  },
};

/**
 * One side of the flow, as its own page.
 *
 * Three journeys in one page meant scrolling past the CAM's tables and the
 * family's phone to find the provider's four frames, and the menu offered one
 * row for all of it. Every flow and every state here is tagged with exactly one
 * journey, so a page per journey is a filter rather than a copy: the content
 * lives once and cannot drift between them.
 *
 * Links and the component list stay on all three. They describe the feature,
 * not a side of it.
 */
const side = (journey: string, id: string, title: string): HandoverSpec => ({
  ...projectMcSpec,
  id,
  title,
  flows: projectMcSpec.flows.filter((f) => f.journey === journey),
  states: projectMcSpec.states.filter((st) => st.journey === journey),
});

export const projectMcProviderSpec = side(
  'Provider',
  'project-mc-provider',
  'Provider flow',
);

export const projectMcCamSpec = side('CAM', 'project-mc-cam', 'CAM flow');

export const projectMcMemberSpec = side(
  'Member',
  'project-mc-member',
  'Member flow',
);
